import React, { useMemo } from "react";
import { Text, View, Svg, Circle, Path } from "@react-pdf/renderer";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { HistoryLogDescriptionCreator } from "src/modules/3rd-party-management/services/HistoryLogDescriptionCreator";

import styles from "../../Styles";
import { buildManagementSummaryData } from "../../helpers/managementSummary";
import { formatDisplayValue } from "../../helpers";
import { PageReference } from "../PageReference";
import { TOC_ANCHORS } from "../../helpers/tableOfContents";

const logDescriptionCreator = new HistoryLogDescriptionCreator();

const getPercentages = (items = []) => {
    const total = items.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    if (!total) {
        return items.map((item) => ({ ...item, percentage: 0 }));
    }

    return items.map((item) => ({
        ...item,
        percentage: Math.round((Number(item.amount || 0) / total) * 100),
    }));
};

const PercentRing = ({ value = 0 }) => (
    <View style={styles.percentRing}>
        <Text style={styles.percentRingText}>{`${value}%`}</Text>
    </View>
);

const PencilIcon = () => (
    <Svg style={styles.activityIcon} viewBox="0 0 24 24">
        <Path
            d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25zm17.71-10.04a1.004 1.004 0 000-1.42l-2.5-2.5a1.004 1.004 0 00-1.42 0l-1.96 1.96 3.75 3.75 2.13-1.79z"
            fill="#1F2937"
        />
    </Svg>
);

const toRadians = (angle) => (angle * Math.PI) / 180;

const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = toRadians(angleInDegrees - 90);

    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
};

const describeArc = (x, y, radius, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
        "M", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    ].join(" ");
};

const DonutWidget = ({ items = [], t }) => {
    const itemsWithPercentages = getPercentages(items);
    const hasData = itemsWithPercentages.some((item) => item.amount > 0);
    const chartItems = hasData ? itemsWithPercentages.filter((item) => item.percentage > 0) : [];
    let currentAngle = 0;
    const chartLabels = [];

    return (
        <View>
            <View style={styles.donutWrap}>
                <Svg style={styles.donutSvg} viewBox="0 0 92 92">
                    <Circle
                        cx={46}
                        cy={46}
                        r={34}
                        stroke="#E5E7EB"
                        strokeWidth={8}
                        fill="none"
                    />
                    {chartItems.map((item) => {
                        const startAngle = currentAngle;
                        const sliceAngle = (item.percentage / 100) * 360;
                        const endAngle = startAngle + sliceAngle;
                        const path = describeArc(46, 46, 34, currentAngle, endAngle);
                        currentAngle = endAngle;
                        const midPoint = polarToCartesian(46, 46, 50, startAngle + (sliceAngle / 2));
                        chartLabels.push({
                            key: item.title,
                            percentage: item.percentage,
                            x: Math.max(2, Math.min(78, midPoint.x - 7)),
                            y: Math.max(2, Math.min(82, midPoint.y - 4)),
                        });

                        return (
                            <Path
                                key={item.title}
                                d={path}
                                stroke={item.color}
                                strokeWidth={8}
                                fill="none"
                            />
                        );
                    })}
                    <Circle
                        cx={46}
                        cy={46}
                        r={22}
                        fill="#FFFFFF"
                    />
                </Svg>
                {chartLabels.map((label) => (
                    <Text
                        key={label.key}
                        style={[
                            styles.chartPercentageLabel,
                            {
                                left: label.x,
                                top: label.y,
                            },
                        ]}
                    >
                        {`${label.percentage}%`}
                    </Text>
                ))}
            </View>

            {!hasData && <Text style={styles.emptyStateText}>{t("No Data")}</Text>}

            <View style={styles.legendRow}>
                {itemsWithPercentages.map((item) => (
                    <View key={item.title} style={styles.legendItemCompact}>
                        <View style={[styles.legendDot, { backgroundColor: item.color }]} />
                        <Text style={styles.legendLabelCompact}>
                            {t(item.title)}
                        </Text>
                    </View>
                ))}
            </View>
        </View>
    );
};

const SummaryMetricColumn = ({ item, t }) => {
    const renderValue = () => {
        if (item.type === "percent") {
            return <PercentRing value={Number(item.value || 0)} />;
        }

        if (item.riskColor) {
            return (
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <View style={[styles.riskRatingDot, { backgroundColor: item.riskColor }]} />
                    <Text style={[styles.summaryMetricValue, { marginLeft: 4 }]}>
                        {t(item.value)}
                    </Text>
                </View>
            );
        }

        return (
            <Text style={styles.summaryMetricValue}>
                {formatDisplayValue(item.value)}
            </Text>
        );
    };

    return (
        <View style={styles.summaryMetricCol}>
            <Text style={styles.summaryMetricLabel}>{t(item.title)}</Text>
            {renderValue()}
        </View>
    );
};

const RecentActivityList = ({ activities = [], dateUtils, t }) => {
    if (!activities.length) {
        return <Text style={styles.emptyStateText}>{t("No Data")}</Text>;
    }

    return (
        <>
            {activities.slice(0, 5).map((activity, index) => (
                <View key={activity?.id || index} style={styles.activityItem}>
                    <View style={styles.activityRow}>
                        <PencilIcon />
                        <View style={styles.activityBody}>
                            <Text style={styles.activityDate}>
                                {activity?.created_at
                                    ? `${dateUtils.convertDateToDate(activity.created_at, INTERNATIONAL_DATE_FORMAT)} ${dateUtils.convertDateToDate(activity.created_at, "HH:mm")}`
                                    : "-"}
                            </Text>
                            <Text style={styles.activityText}>
                                {activity?.text
                                    ? t(activity.text)
                                    : t(logDescriptionCreator.createFromApiData(activity) || "Update available")}
                            </Text>
                        </View>
                    </View>
                </View>
            ))}
        </>
    );
};

const RiskMatrix = ({ riskMatrixData, t }) => {
    const matrix = riskMatrixData?.matrix;
    const consequences = riskMatrixData?.consequences || [];
    const likelihoods = riskMatrixData?.likelihoods || [];

    if (!matrix?.length) {
        return null;
    }

    const renderRiskBullets = (count = 0) => {
        const visibleCount = Math.min(count, 10);

        return Array.from({ length: visibleCount }).map((_, index) => (
            <View key={index} style={styles.matrixRiskBullet} />
        ));
    };

    return (
        <View wrap={false}>
            <Text style={styles.riskMatrixTitle}>{t("Risk Matrix")}</Text>

            <View style={styles.riskMatrixTableWrap}>
                <View style={styles.riskMatrixTable}>
                <View style={styles.riskMatrixHeaderRow}>
                    <View style={styles.riskMatrixAxisLabelCell} />
                    <View style={styles.riskMatrixAxisValueCell} />
                    <View style={styles.riskMatrixConsequenceBand}>
                        <Text style={styles.riskMatrixConsequenceText}>{t("Consequence")}</Text>
                    </View>
                </View>

                <View style={styles.riskMatrixHeaderRow}>
                    <View style={styles.riskMatrixAxisLabelCell} />
                    <View style={styles.riskMatrixAxisValueCell} />
                    {consequences.map((consequence) => (
                        <View key={consequence.id} style={styles.riskMatrixXAxisCell}>
                            <Text style={styles.riskMatrixXAxisText}>
                                {`> ${(Number(consequence?.title || 0) / 1000).toLocaleString("en-US")}K`}
                            </Text>
                        </View>
                    ))}
                </View>

                <View style={styles.riskMatrixBodyRow}>
                    <View
                        style={[
                            styles.riskMatrixLikelihoodAxisLabelColumn,
                            { height: matrix.length * 47 },
                        ]}
                    >
                        <Text style={styles.riskMatrixLikelihoodText}>{t("Likelihood")}</Text>
                    </View>

                    <View style={styles.riskMatrixDataRows}>
                        {matrix.map((row, rowIndex) => (
                            <View key={rowIndex} style={styles.riskMatrixRow}>
                                <View style={styles.riskMatrixYAxisCell}>
                                    <Text style={styles.riskMatrixYAxisText}>
                                        {`> ${likelihoods[rowIndex]?.label || 0}%`}
                                    </Text>
                                </View>

                                {row.map((cell) => {
                                    const riskCount = cell?.risks?.length || 0;

                                    return (
                                        <View
                                            key={cell.id}
                                            style={[
                                                styles.riskMatrixCell,
                                                { backgroundColor: `#${cell?.color || "FFFFFF"}` },
                                            ]}
                                        >
                                            <View style={styles.matrixRiskCellBottom}>
                                                <View style={styles.matrixBulletsWrap}>
                                                    {riskCount > 0 && renderRiskBullets(riskCount)}
                                                </View>
                                                {riskCount > 0 && (
                                                    <View style={styles.matrixCountWrap}>
                                                        <Text style={styles.riskMatrixCellText}>{riskCount}</Text>
                                                    </View>
                                                )}
                                            </View>
                                        </View>
                                    );
                                })}
                            </View>
                        ))}
                    </View>
                </View>
            </View>
            </View>
        </View>
    );
};

const ManagementSummary = ({
    t,
    supplierData,
    overviewHeader,
    statistics,
    lastActivities,
    riskMatrixData,
    isOwnCompany,
}) => {
    const dateUtils = useMemo(() => new DateUtils(), []);
    const resolvedOverviewHeader = overviewHeader || supplierData?.overviewHeader || {};
    const resolvedStatistics = statistics || supplierData?.statistics || {};
    const resolvedLastActivities = lastActivities || supplierData?.lastActivities || [];
    const resolvedRiskMatrixData = riskMatrixData || supplierData?.riskMatrix || null;

    const summaryData = useMemo(
        () => buildManagementSummaryData({
            supplierData,
            overviewHeader: resolvedOverviewHeader,
            statistics: resolvedStatistics,
            lastActivities: resolvedLastActivities,
            riskMatrixData: resolvedRiskMatrixData,
            dateUtils,
            t,
            isOwnCompany,
        }),
        [
            supplierData,
            resolvedOverviewHeader,
            resolvedStatistics,
            resolvedLastActivities,
            resolvedRiskMatrixData,
            dateUtils,
            t,
            isOwnCompany,
        ]
    );

    return (
        <View style={styles.managementSummaryContent}>
            <PageReference id={TOC_ANCHORS.MANAGEMENT_SUMMARY} />
            <View style={styles.managementSummaryBox}>
                <Text style={styles.managementSummaryTitle}>
                    {t("Management Summary")}
                </Text>

                <View style={styles.summaryMetricsRow}>
                    {summaryData.firstRow.map((item) => (
                        <SummaryMetricColumn key={item.title} item={item} t={t} />
                    ))}
                </View>

                <View style={styles.summaryMetricsRow}>
                    {summaryData.secondRow.map((item) => (
                        <SummaryMetricColumn key={item.title} item={item} t={t} />
                    ))}
                </View>
            </View>

            <View style={styles.analyticsRow}>
                <View style={styles.analyticsColumn}>
                    <Text style={styles.analyticsTitle}>
                        {t(isOwnCompany ? "Company Risk Level" : "Third Party Risk Level")}
                    </Text>
                    <DonutWidget items={summaryData.riskLevels} t={t} />
                </View>

                <View style={styles.analyticsColumn}>
                    <Text style={styles.analyticsTitle}>{t("Tasks")}</Text>
                    <DonutWidget items={summaryData.tasks} t={t} />
                </View>

                <View style={[styles.analyticsColumn, styles.analyticsColumnLast]}>
                    <Text style={styles.analyticsTitle}>{t("Recent Activity")}</Text>
                    <RecentActivityList
                        activities={summaryData.recentActivities}
                        dateUtils={dateUtils}
                        t={t}
                    />
                </View>
            </View>

            <RiskMatrix riskMatrixData={summaryData.riskMatrixData} t={t} />
        </View>
    );
};

export default ManagementSummary;
